import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FUNNEL_TYPE } from '../../../../enum/constants';
import { IChatItem } from '../../../../interfaces';
import { TaskService } from '../../../../services/task.service';
import { CardReminderXData } from '../../board-v3.types';
import { CardGeneralTab } from '../../../tabs/enums/tabs.enum';
import { Tab } from '../../../tabs-v2/interfaces/tab.interface';
import { TabsV2Component } from '../../../tabs-v2/tabs.component';
import { CardReminderPrefill } from './card-case-panel/card-case-panel.component';
import { BoardV3CardChatsPanelComponent } from './card-chats-panel/card-chats-panel.component';

export interface CardManagerOption {
  id: string | number;
  name: string;
  avatarUrl?: string;
}

@Component({
  selector: 'app-board-v3-card-general-panel',
  templateUrl: './card-general-panel.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class BoardV3CardGeneralPanelComponent implements OnChanges {
  @Input() cardId!: string;
  /** The name of the card and whether it is a lead or a deal: a task created from the card links to it */
  @Input() cardName = '';
  @Input() funnelType: FUNNEL_TYPE | null = null;
  @Input() managers: CardManagerOption[] = [];
  @Input() chats: IChatItem[] = [];
  @Output() commentAdded = new EventEmitter<unknown>();
  @Output() reminderAdded = new EventEmitter<unknown>();
  @ViewChild('tabsRef') tabsRef!: TabsV2Component;
  @ViewChild(BoardV3CardChatsPanelComponent) private chatsPanel?: BoardV3CardChatsPanelComponent;

  private readonly taskService = inject(TaskService);
  private readonly destroyRef = inject(DestroyRef);

  readonly CardGeneralTab = CardGeneralTab;
  selectedTab!: Tab<CardGeneralTab>;
  reminderPrefill: CardReminderPrefill | null = null;
  /** The tasks linked to the card: known before the tab is opened, so the counter shows what is behind it */
  tasksTotal?: number;

  get tabs(): Tab<CardGeneralTab>[] {
    return [
      { name: 'case_tab', value: CardGeneralTab.CASE, default: true },
      { name: 'comments', value: CardGeneralTab.COMMENTS, default: false },
      { name: 'chats', value: CardGeneralTab.CHATS, default: false, count: this.chats.length },
      { name: 'calls_tab', value: CardGeneralTab.CALLS, default: false },
      { name: 'tasks', value: CardGeneralTab.TASKS, default: false, count: this.tasksTotal },
    ];
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['cardId'] && this.cardId) this.loadTasksTotal();
  }

  reloadChats(): void {
    this.chatsPanel?.reload();
  }

  onSelectTab(tab: Tab<CardGeneralTab>): void {
    this.selectedTab = tab;
  }

  prefillReminder(data: CardReminderXData): void {
    this.openCaseTab();
    this.reminderPrefill = { data, editing: false };
  }

  prefillReminderForEdit(data: CardReminderXData): void {
    this.openCaseTab();
    this.reminderPrefill = { data, editing: true };
  }

  /** Only the counter: the tasks themselves are loaded when the tab is opened. Without the count the tab still works */
  private loadTasksTotal(): void {
    const cardId = this.cardId;
    this.tasksTotal = undefined;
    this.taskService
      .getTasks({ entity_type: 'card', entity_id: cardId, take: 1 })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ total }) => {
          if (this.cardId === cardId) this.tasksTotal = total;
        },
        error: () => undefined,
      });
  }

  private openCaseTab(): void {
    if (this.selectedTab?.value === CardGeneralTab.CASE) return;

    const caseTab = this.tabs.find((tab) => tab.value === CardGeneralTab.CASE);
    if (caseTab) {
      this.tabsRef.changeTab(caseTab);
    }
  }
}
